import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { FileSignature, Check, FileText } from "lucide-react";
import WaiverModal from "../components/WaiverModal";
import TaxFormModal from "../components/TaxFormModal";

export default function ApplyCreator() {
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({ display_name: "", bio: "", category: "films", portfolio_url: "", sample_links: "" });
  const [existing, setExisting] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [waiver, setWaiver] = useState(null);
  const [taxStatus, setTaxStatus] = useState(null);
  const [waiverOpen, setWaiverOpen] = useState(false);
  const [taxOpen, setTaxOpen] = useState(false);

  const loadWaiver = () => api.get("/waiver/status").then((r) => setWaiver(r.data)).catch(() => {});
  const loadTax = () => api.get("/creator/tax-form/status").then((r) => setTaxStatus(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data));
    api.get("/creator/my-application").then((r) => setExisting(r.data)).catch(() => {});
    loadWaiver();
    loadTax();
  }, []);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    if (!waiver?.accepted) {
      setWaiverOpen(true);
      setBusy(false);
      return;
    }
    try {
      const body = { ...form, sample_links: form.sample_links.split("\n").filter(Boolean) };
      await api.post("/creator/apply", body);
      toast.success("Application submitted");
      const r = await api.get("/creator/my-application");
      setExisting(r.data);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const pending = existing.find((a) => a.status === "pending");

  return (
    <div data-testid="apply-creator-page" className="pt-32 pb-20 px-4 sm:px-8 md:px-12 max-w-3xl mx-auto">
      <WaiverModal
        open={waiverOpen}
        onClose={() => setWaiverOpen(false)}
        onAccepted={() => { setWaiverOpen(false); loadWaiver(); toast.info("Agreement signed. Please submit your application again."); }}
      />
      <TaxFormModal
        open={taxOpen}
        onClose={() => setTaxOpen(false)}
        onSubmitted={() => { setTaxOpen(false); loadTax(); }}
      />

      <div className="text-xs uppercase tracking-[0.3em] text-tv-accent font-semibold">Creator Application</div>
      <h1 className="font-display font-black text-5xl text-white tracking-tighter mt-2">Become a Tap In TV creator</h1>
      <p className="text-white/70 mt-3">Upload films, music, shows, and more. Premium tier unlocks live broadcasting & Director's Mode.</p>

      {/* Creator Waiver status card */}
      <div
        data-testid="apply-waiver-card"
        className={`mt-8 rounded-2xl border p-5 flex items-start gap-4 flex-wrap ${waiver?.accepted ? "border-emerald-500/30 bg-emerald-500/5" : "border-tv-accent/40 bg-tv-accent/5"}`}
      >
        <FileSignature className={`w-6 h-6 shrink-0 ${waiver?.accepted ? "text-emerald-300" : "text-tv-accent"}`} />
        <div className="flex-1 min-w-[240px]">
          <div className="font-display font-bold text-white tracking-tight">
            {waiver?.accepted ? "Creator Waiver active" : "Creator Waiver & Agreement required"}
          </div>
          {waiver?.accepted ? (
            <div className="text-white/70 text-sm mt-1">
              Signed as <b className="text-white">{waiver.signature}</b>. Renews on {new Date(waiver.expires_at).toLocaleDateString()} ({waiver.duration_days} days after sign).
            </div>
          ) : (
            <div className="text-white/70 text-sm mt-1">
              Sign the quarterly agreement to unlock upload, live streaming, and monetization. Takes about a minute.
            </div>
          )}
        </div>
        <button
          data-testid="apply-waiver-btn"
          onClick={() => setWaiverOpen(true)}
          className={`font-bold px-5 py-2.5 rounded-full text-sm inline-flex items-center gap-2 ${
            waiver?.accepted ? "bg-white/10 hover:bg-white/20 text-white" : "bg-tv-accent hover:bg-yellow-400 text-black"
          }`}
        >
          {waiver?.accepted ? <><Check className="w-4 h-4" /> Review / Re-sign</> : <><FileSignature className="w-4 h-4" /> Sign agreement</>}
        </button>
      </div>

      {/* Tax form status card */}
      <div
        data-testid="apply-tax-card"
        className={`mt-4 rounded-2xl border p-5 flex items-start gap-4 flex-wrap ${taxStatus?.submitted ? "border-emerald-500/30 bg-emerald-500/5" : "border-white/10 bg-white/[0.03]"}`}
      >
        <FileText className={`w-6 h-6 shrink-0 ${taxStatus?.submitted ? "text-emerald-300" : "text-tv-accent"}`} />
        <div className="flex-1 min-w-[240px]">
          <div className="font-display font-bold text-white tracking-tight flex items-center gap-2">
            {taxStatus?.submitted ? "Tax form on file" : "Tax information (optional now, required at $600)"}
          </div>
          {taxStatus?.submitted ? (
            <div className="text-white/70 text-sm mt-1">
              {taxStatus.form.form_type} on file ending <b className="text-white">••••{taxStatus.form.tin_last4}</b>. Payouts unblocked.
            </div>
          ) : (
            <div className="text-white/70 text-sm mt-1">
              File your W-9 / W-8BEN now to skip the pause when you cross ${taxStatus?.threshold_usd || 600} in lifetime earnings. Your TIN is encrypted at rest; only the last 4 digits are ever displayed.
            </div>
          )}
        </div>
        <button
          data-testid="apply-tax-btn"
          onClick={() => setTaxOpen(true)}
          className={`font-bold px-5 py-2.5 rounded-full text-sm inline-flex items-center gap-2 ${
            taxStatus?.submitted ? "bg-white/10 hover:bg-white/20 text-white" : "bg-tv-accent hover:bg-yellow-400 text-black"
          }`}
        >
          {taxStatus?.submitted ? <><Check className="w-4 h-4" /> Update</> : <><FileText className="w-4 h-4" /> File tax form</>}
        </button>
      </div>

      {existing.length > 0 && (
        <div className="mt-8 space-y-2">
          <h2 className="font-display font-bold text-xl text-white">Your applications</h2>
          {existing.map((a) => (
            <div key={a.id} className="tv-glass rounded-xl p-4 flex justify-between items-center">
              <div>
                <div className="text-white font-semibold">{a.display_name}</div>
                <div className="text-xs text-white/50 uppercase tracking-wider">{a.category}</div>
              </div>
              <div className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${a.status === "approved" ? "bg-green-500/20 text-green-400" : a.status === "denied" ? "bg-red-500/20 text-red-400" : "bg-yellow-500/20 text-yellow-300"}`}>
                {a.status}
              </div>
            </div>
          ))}
        </div>
      )}

      {!pending && (
        <form onSubmit={submit} className="mt-10 tv-glass rounded-2xl p-6 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-white/60 font-semibold">Display Name</label>
            <input data-testid="apply-display-name" required value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white outline-none focus:border-tv-brand" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/60 font-semibold">Primary Category</label>
            <select data-testid="apply-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 w-full bg-[#0B1120] border border-white/10 rounded-lg px-3 py-2.5 text-white outline-none focus:border-tv-brand">
              {cats.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/60 font-semibold">Bio (min 20 chars)</label>
            <textarea data-testid="apply-bio" required minLength={20} rows={4} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white outline-none focus:border-tv-brand" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/60 font-semibold">Portfolio URL</label>
            <input data-testid="apply-portfolio" value={form.portfolio_url} onChange={(e) => setForm({ ...form, portfolio_url: e.target.value })} className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white outline-none focus:border-tv-brand" placeholder="https://..." />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/60 font-semibold">Sample links (one per line)</label>
            <textarea data-testid="apply-samples" rows={3} value={form.sample_links} onChange={(e) => setForm({ ...form, sample_links: e.target.value })} className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white outline-none focus:border-tv-brand" />
          </div>
          {err && <div className="text-red-400 text-sm">{err}</div>}
          <button data-testid="apply-submit" disabled={busy} className="bg-tv-accent text-black font-bold px-6 py-3 rounded-full disabled:opacity-60">
            {busy ? "Submitting…" : waiver?.accepted ? "Submit application" : "Sign agreement, then submit"}
          </button>
        </form>
      )}
    </div>
  );
}
